'use client';

import { useIntl } from 'react-intl';
import {
  useContentSize,
  useEmbedContext,
  useUserContext,
} from '@osmosis/nextjs-components/context';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { track } from '../../lib/util/tracking';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare';
import { faArrowLeftToLine } from '@fortawesome/pro-solid-svg-icons/faArrowLeftToLine';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { dashboardAIbutton, text0 } from '@osmosis/tailwind-ui/styles';
import { KeyboardEvent, ReactNode, forwardRef, useEffect } from 'react';
import useModal from '@osmosis/tailwind-ui/hooks/useModal';
import LearnLockoutModal from '../learn/LearnLockoutModal';
import { LockoutModalVariant } from '../../constants/lockout';
import { useAIPanelContext } from '../../context/AIPanelContext';
import useOsmosisAI from '../../hooks/useOsmosisAI';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';

type Props = Readonly<{
  searchQuery?: string;
  variant?: 'default' | 'dashboard';
  showLoggedOut?: boolean;
  enableEmbeddedChat?: boolean;
  /** Override the button label. Defaults to 'Ask Osmosis AI'. */
  label?: string;
  /** Override the accessible name. Defaults to `label`, so the announced name matches what's visible. */
  ariaLabel?: string;
  /** Icon rendered to the left of the label. */
  leftIcon?: ReactNode;
  /** Icon rendered to the right of the label. Defaults to the arrow icon when leftIcon is not set. */
  rightIcon?: ReactNode;
  classNames?: {
    container?: string;
    button?: string;
    text?: string;
  };
  skipShowTracking?: boolean;
  source?: string;
  pageContext?: string;
  shouldInitFreshChat?: boolean;
  /** Placement of the button on a page. */
  placement?: string;
  persistButtonVisible?: boolean;
  handleClickOverride?: () => void;
  forceShowLockoutForLoggedOut?: boolean;
  /** Passthrough a11y attributes, for example when used as a tab in a tablist. */
  id?: string;
  role?: string;
  'aria-selected'?: boolean;
  'aria-controls'?: string;
  tabIndex?: number;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
}>;

/**
 * Osmosis AI Button Component.
 */
const OsmosisAIButton = forwardRef<HTMLButtonElement, Props>(
  function OsmosisAIButton(
    {
      searchQuery,
      variant = 'default',
      showLoggedOut,
      enableEmbeddedChat = true,
      label,
      ariaLabel,
      leftIcon,
      rightIcon,
      classNames,
      skipShowTracking,
      source,
      pageContext,
      shouldInitFreshChat = true,
      placement,
      persistButtonVisible,
      handleClickOverride,
      forceShowLockoutForLoggedOut,
      id,
      role,
      'aria-selected': ariaSelected,
      'aria-controls': ariaControls,
      tabIndex,
      onKeyDown,
    }: Props,
    ref,
  ) {
    const { getPageLocation } = useEmbedContext();
    const { pathname } = getPageLocation();
    const { openPanel, isOpen, setIsAILockoutOpen, shouldRenderAIChat } =
      useAIPanelContext();
    const { hasExpiredPrime } = useUserContext();
    const { getClassValue } = useContentSize();
    const { openInNewTab, shouldShowLockout, showAIButton, isLoggedOut } =
      useOsmosisAI(showLoggedOut);
    const lockoutModal = useModal();
    const intl = useIntl();

    const chatIsEnabled = enableEmbeddedChat;

    const lockoutHeaderMessage = hasExpiredPrime
      ? intl.formatMessage({
          id: 'learn.lockout.ai.expired',
          defaultMessage: 'Continue your conversation.',
        })
      : intl.formatMessage({
          id: 'learn.lockout.ai',
          defaultMessage:
            'Learn side by side about any medical topic with Osmosis AI',
        });

    const lockoutBodyMessage = hasExpiredPrime
      ? intl.formatMessage({
          id: 'learn.lockout.ai.body',
          defaultMessage:
            'Purchase a plan to unlock Osmosis AI chat and personalized learning support.',
        })
      : intl.formatMessage(
          {
            id: 'learn.lockout.ai.body.logged-out',
            defaultMessage:
              'Create an account to get <span>7 days of access</span> and use Osmosis AI to dive deeper into any medical topic + explore our content library and features.',
          },
          {
            span: (chunks) => (
              <span
                key="span"
                style={{
                  color: '#004E96',
                }}
              >
                {chunks}
              </span>
            ),
          },
        );

    const showLockout = () => {
      track(
        'osmosis-ai-lockout',
        {
          page: pathname,
          source,
          placement,
        },
        ['keen', 'pendo'],
      );
      lockoutModal.show();
    };

    useEffect(() => {
      setIsAILockoutOpen(lockoutModal.isVisible);
    }, [lockoutModal.isVisible]);

    useEffect(() => {
      if (!showAIButton || skipShowTracking) return;
      // Avoid tracking if the component is unmounted quickly
      const id = setTimeout(() => {
        track(
          'osmosis-ai-button-shown',
          {
            page: pathname,
            source,
            placement,
          },
          ['keen', 'pendo'],
        );
      }, 500);

      return () => clearTimeout(id);
    }, [showAIButton, skipShowTracking]);

    if (!showAIButton) return null;

    // Locked-out users have no rendered chat, so keep the button to reach the lockout popup.
    if (chatIsEnabled && isOpen && shouldRenderAIChat && !persistButtonVisible)
      return null;

    const handleClick = async () => {
      if (shouldShowLockout || (isLoggedOut && forceShowLockoutForLoggedOut)) {
        return showLockout();
      }
      if (handleClickOverride) return handleClickOverride();

      track(
        'osmosis-ai-button-clicked',
        {
          page: pathname,
          source,
          placement,
        },
        ['keen', 'pendo'],
      );

      if (chatIsEnabled) {
        openPanel(searchQuery, {
          shouldInitFreshChat,
        });
        return;
      }

      await openInNewTab({
        searchQuery,
        pageContext,
        shouldInitFreshChat,
      });
    };

    const isDashboard = variant === 'dashboard';

    const defaultLabel = intl.formatMessage({
      id: 'osmosis-ai-btn.label',
      defaultMessage: 'Ask Osmosis AI',
    });

    const buttonClassname = cn(
      dashboardAIbutton,
      'w-full border-1 rounded-[8px]',
      isDashboard ? 'h-[55px]' : 'h-[42px]',
      classNames?.button || '',
    );

    const responsiveContainerClassname = getClassValue({
      defaultClasses: isDashboard ? 'sm:w-[156px]' : 'w-[166px] md:w-44',
      breakpoints: isDashboard
        ? { sm: 'w-[156px]' }
        : { md: 'w-44', base: 'w-[166px]' },
    });
    const containerClassname = cn(
      `w-full`,
      responsiveContainerClassname,
      classNames?.container || '',
    );

    return (
      <Flex className={containerClassname}>
        <Button
          ref={ref}
          data-testid="osmosis-ai-btn"
          onClick={() => handleClick()}
          className={buttonClassname}
          aria-label={ariaLabel ?? label ?? defaultLabel}
          id={id}
          role={role}
          aria-selected={ariaSelected}
          aria-controls={ariaControls}
          tabIndex={tabIndex}
          onKeyDown={onKeyDown}
        >
          {leftIcon}
          <P
            className={cn(
              `relative z-10 flex items-center whitespace-nowrap font-bold`,
              text0,
              classNames?.text,
            )}
          >
            {label ?? defaultLabel}
          </P>
          {rightIcon ??
            (!leftIcon && (
              <Icon
                icon={
                  chatIsEnabled ? faArrowLeftToLine : faArrowUpRightFromSquare
                }
                className={`ml-1 ${text0}`}
                size={16}
              />
            ))}
        </Button>
        <LearnLockoutModal
          modalProps={{ ...lockoutModal, _className: 'max-w-[600px] w-full' }}
          headerMessage={lockoutHeaderMessage}
          bodyMessage={lockoutBodyMessage}
          identifier="osmosis-ai"
          variant={
            hasExpiredPrime
              ? LockoutModalVariant.EXPIRED_PRIME_AI
              : LockoutModalVariant.DEFAULT_AI
          }
        />
      </Flex>
    );
  },
);

export default OsmosisAIButton;
